<template>
  <div
    :class="[
      'form-loader',
      { '-wrapped': wrapped },
    ]">
    <div
      v-if="title || abstract"
      class="form-loader__text">
      <h3
        :class="[
          'form-loader__title',
          { '-big': titleBig },
        ]">
        {{ title }}
      </h3>
      <div
        class="form-loader__abstract"
        v-html="abstract" />
    </div>

    <template v-if="data">
      <form
        v-show="formStatus !== 1"
        novalidate
        autocomplete="false"
        class="form-loader__body"
        @focusin="ensureRecaptcha"
        @submit="handleSubmission">
        <template
          v-for="(row, idx) in computedRows"
          :key="idx">
          <slot
            v-if="$slots['extra-fields'] && extraFieldsBeforeEnd > 0 && idx === computedRows.length - extraFieldsBeforeEnd"
            name="extra-fields" />
          <div class="form-loader__row">
            <template
              v-for="(field, jdx) in row.fields"
              :key="jdx">
              <div
                v-if="isFieldVisible(field)"
                :class="['form-loader__field', `-col-${12 / row.fields.length}`, { '-hidden': field.visibility == 'hidden' }]">
                <component
                  :is="components[getFieldComponent(field)]"
                  v-bind="{
                    ...field,
                    // per i campi Date l'obbligatorieta' vive nei sotto-campi: senza questo
                    // l'asterisco mancherebbe su campi che il server rifiuta se vuoti
                    required: isFieldRequired(field),
                    outerLabel: true,
                    sync: true,
                    label: fieldLabel(field),
                    placeholder: $t(`forms.placeholder.${field.name}`) || field.label,
                    id: field.name,
                    index: jdx,
                    multiple: field.limitFiles,
                    disabled: props.disabled || isSubmitting || field.disabled,
                    mask: dynamicMask(field.name),
                    ...(getAppointmentDateMin(field) ? { min: getAppointmentDateMin(field) } : {}),
                    ...(field.visibility == 'hidden' ? { type: 'hidden' } : {}),
                    ...(getFieldOptions(field) ? { options: getFieldOptions(field) } : {}),
                  }" />
              </div>
            </template>
          </div>
        </template>
        <div v-if="hiddenFields">
          <input-hidden
            v-for="field in hiddenFields"
            :key="field.id"
            v-bind="{
              ...field,
              id: field.name,
              disabled: props.disabled || isSubmitting || field.disabled,
            }" />
        </div>
        <!-- Invio rifiutato dal server (es. validazione lato Formie): senza questo blocco
             il click su INVIA non produce alcun riscontro visibile e la richiesta va persa -->
        <div
          v-show="formStatus === -1"
          class="form-loader__row">
          <div
            class="form-loader__field -col-12 form-loader__error"
            role="alert"
            aria-live="assertive">
            <strong>{{ $t('forms.error.title') }}</strong>
            <div>{{ $t('forms.error.message') }}</div>
          </div>
        </div>
        <div class="form-loader__row -submit">
          <div
            :class="[
              'form-loader__field',
              '-col-12',
              `-align-${pageSettings?.buttonsPosition}`,
            ]">
            <cta
              type="submit"
              :text="submitText || $t('forms.labels.confirm')"
              variant="solid"
              :color="ctaColor"
              :show-icon="true"
              :animated-icon="true"
              :disabled="props.disabled || props.submitDisabled || isSubmitting"
              :aria-label="submitText || $t('forms.labels.confirm')" />
          </div>
        </div>
      </form>
      <div
        v-show="formStatus === 1"
        class="form-loader__submitted">
        <h4>{{ successTitle || $t('forms.success.title') }}</h4>
        <div>{{ successMessage || $t('forms.success.message') }}</div>
      </div>
    </template>
  </div>
</template>

  <script setup>
  // import { useGtm } from '@gtm-support/vue-gtm'
  import { useForm } from 'vee-validate'
  import {
    object, setLocale, string as yupString
  } from 'yup'

  import { useStoreSelection } from '~/composables/useStoreSelection'
  import { formQuery } from '~/graphql/form'
  import { getStoreSearchDisplayTitle } from '~/utils/stores'

  const emit = defineEmits([
    'load',
    'submitted',
    'submitting'
  ])

  const { $graphql } = useNuxtApp()
  const { public: runtimePublic } = useRuntimeConfig()
  const {
    t, te, locale
  } = useI18n()
  const {
    componentMatcher,
    createFormSchema,
    createFormQuery,
    isFieldRequired,
    loadRecaptcha,
    executeRecaptcha
  } = useFormHelpers()

  // const gtm = useGtm()

  const props = defineProps({
    title: String,
    abstract: String,
    handle: String,
    titleBig: Boolean,
    params: Array,
    wrapped: {
      type: Boolean,
      default: true
    },
    ctaColor: {
      type: String,
      default: 'white'
    },
    /**
     * When true, the Italy option is hidden from the country dropdown.
     * Pass this prop when the form is used in an "estero" (abroad) context where
     * only foreign countries should be selectable.
     */
    storeLocatorExcludeItaly: {
      type: Boolean,
      default: false
    },
    /**
     * Override options for specific fields by handle.
     * Map of fieldHandle → Array<{ value, label }>.
     * Takes priority over Formie-defined options.
     */
    fieldOptionsOverrides: {
      type: Object,
      default: () => ({})
    },
    keepSuccessMessage: {
      type: Boolean,
      default: false
    },
    successTitle: {
      type: String,
      default: ''
    },
    successMessage: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Disables only the submit button (not the form fields).
     * Useful when external conditions (e.g. store selection) must be met before submission.
     */
    submitDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Position for the #extra-fields slot, counted from the end of the field rows.
     * e.g. 2 = insert before the 2nd-to-last row. 0 = disabled (no slot rendering).
     */
    extraFieldsBeforeEnd: {
      type: Number,
      default: 0
    },
    submitText: {
      type: String,
      default: ''
    }
  })

  const components = {
    'InputCheckbox': resolveComponent('InputCheckbox'),
    'InputText': resolveComponent('InputText'),
    'InputPhone': resolveComponent('InputPhone'),
    'InputTextarea': resolveComponent('InputTextarea'),
    'InputSelectNative': resolveComponent('InputSelectNative'),
    'InputFile': resolveComponent('InputFile'),
    'InputDate': resolveComponent('InputDate'),
    'FormHtmlContent': resolveComponent('FormHtmlContent')
  }

  const inputMasks = {
    'fullName': {
      'data-maska': 'A',
      'data-maska-tokens': 'A:[a-zA-ZÀ-ú ]:multiple'
    }
  }

  const dynamicMask = fieldHandle => {
    return inputMasks[fieldHandle] ?? {}
  }

  const { data } = await useAsyncData(
    `formbuilder-form-${props.handle}-${locale.value}`, () => useApi(formQuery, { handle: props.handle }),
    { transform: r => r.form }
  )

  /**
   * Store-locator integration
   *
   * Some Formie forms (afterSalesSupport, reservedArea) include a country → province → store
   * cascade managed by useStoreSelection. The field handles that activate this behaviour are
   * defined below. The logic is completely inert for any form that does not contain these handles.
   *
   * Field setup in Formie (CMS):
   *  - dealerCountry  → Field_Entries (storeLocatorCountries section) → GQL type [Int]
   *  - dealerProvince → Field_SingleLineText (NOT Dropdown, to avoid Formie validating against
   *                     an empty options list) → options injected at runtime by useStoreSelection
   *  - dealerStore    → Field_SingleLineText (same reason as above) → options injected at runtime;
   *                     submission value is the human-readable "Name, Address" string, not an ID
   *
   * ITALY_ENTRY_ID is the Craft entry ID for "Italia" in the storeLocatorCountries section.
   * It is used as the select value for Italy so that:
   *  a) the Field_Entries mutation receives a valid [Int] array
   *  b) the province select is shown only when Italy is selected (yup.when + isFieldVisible)
   *  c) the italian locale pre-selects Italy automatically
   *
   * Configured via ITALY_ENTRY_ID env var; see configuration/env.js.
   */
  const ITALY_ENTRY_ID = runtimePublic.ITALY_ENTRY_ID
  const STORE_LOCATOR_COUNTRY_HANDLES = [ 'dealerCountry' ]
  const STORE_LOCATOR_PROVINCE_HANDLES = [ 'dealerProvince' ]
  const STORE_LOCATOR_STORE_HANDLES = [ 'dealerStore' ]
  const STORE_LOCATOR_HANDLES = [
    ...STORE_LOCATOR_COUNTRY_HANDLES,
    ...STORE_LOCATOR_PROVINCE_HANDLES,
    ...STORE_LOCATOR_STORE_HANDLES
  ]

  const allFieldHandles = data.value ? data.value.rows.flatMap(r => r.fields?.map(f => f.name) || []) : []
  const hasStoreLocator = STORE_LOCATOR_HANDLES.some(h => allFieldHandles.includes(h))
  const countryHandle = STORE_LOCATOR_COUNTRY_HANDLES.find(h => allFieldHandles.includes(h)) ?? null
  const provinceHandle = STORE_LOCATOR_PROVINCE_HANDLES.find(h => allFieldHandles.includes(h)) ?? null
  const storeHandle = STORE_LOCATOR_STORE_HANDLES.find(h => allFieldHandles.includes(h)) ?? null

  /**
   * Maps field handle → Formie typeName for all fields in this form.
   * Passed to createFormQuery so it can emit the correct GQL scalar type for each variable
   * (e.g. [Int] for Field_Entries instead of the default String from the Yup schema).
   */
  const fieldTypeNameMap = data.value
    ? data.value.rows.reduce((acc, row) => {
      row.fields?.forEach(f => { acc[f.name] = f.typeName })
      return acc
    }, {})
    : {}

  // Track time-only Date fields (isTime && !isDate) to type them as String instead of DateTime
  const timeOnlyFields = data.value
    ? new Set(data.value.rows.flatMap(row =>
      (row.fields || []).filter(f => f.typeName === 'Field_Date' && f.isTime && !f.isDate).map(f => f.name)
    ))
    : new Set()

  // L'appuntamento non puo' essere richiesto per una data gia' trascorsa. Il
  // valore viene calcolato nel browser per rispettare il fuso orario locale.
  const appointmentMinimumDate = ref('')
  onMounted(() => {
    const today = new Date()
    appointmentMinimumDate.value = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, '0'),
      String(today.getDate()).padStart(2, '0')
    ].join('-')
  })

  const getAppointmentDateMin = field => (
    field.name === 'appointmentDate' ? appointmentMinimumDate.value : ''
  )

  const pageSettings = computed(() => data.value ? getFirst(data.value.pages).settings : null)
  const computedRows = computed(() => {
    return data.value
      ? data.value.rows.filter(r => r.fields?.some(f =>
        (!f.visibility || f.visibility !== 'hidden') && !f.typeName?.includes('Hidden'))).map(f => ({ ...f, fields: f.fields?.map(p => ({ ...p, disabled: props?.params?.some(a => a.key === p.name) && props?.params?.find(a => a.key == p.name)?.disabled })) }
      ))
      : null
  })
  const hiddenFields = computed(() => data.value ? data.value.rows?.reduce((acc, r) => acc.concat(r.fields?.filter(f => f.typeName?.includes('Hidden') || f.visibility?.includes('hidden'))), []) : null)

  const recaptchaSettings = computed(() => {
    const _recaptcha = data.value?.settings?.integrations.find(s => s.handle === 'recaptcha')

    if (!_recaptcha || !_recaptcha.enabled) { return null }
    return JSON.parse(_recaptcha.settings)
  })

  // Defer reCAPTCHA (≈375 KB + main-thread cost) until the user actually starts
  // filling the form. On submit executeRecaptcha() loads it anyway if still missing,
  // so nothing breaks; idle visitors (and Lighthouse) never pay for it.
  const ensureRecaptcha = () => {
    if (recaptchaSettings.value) { loadRecaptcha(recaptchaSettings.value.siteKey) }
  }

  setLocale({
    mixed: {
      default: t('forms.errors.default'),
      required: t('forms.errors.required')
    },
    string: { email: t('forms.errors.email') }
  })

  let formSchema = data.value ? createFormSchema(data.value.rows) : null

  /**
   * Override Yup rules for store-locator fields after createFormSchema runs.
   *
   * dealerProvince: required only when Italy is selected (identified by ITALY_ENTRY_ID).
   *   For non-Italian countries there is no province step.
   *
   * dealerStore: required whenever a country is chosen.
   *   The field is hidden via v-if (isFieldVisible) when no country is selected, but
   *   vee-validate evaluates the static schema even for unmounted fields, so we rely on
   *   yup.when to suppress the required error when no country is set.
   */
  if (hasStoreLocator && formSchema && countryHandle) {
    if (provinceHandle && formSchema[provinceHandle] !== undefined) {
      formSchema[provinceHandle] = yupString().when(countryHandle, {
        is: ITALY_ENTRY_ID,
        then: s => s.required(),
        otherwise: s => s.notRequired()
      })
    }
    if (storeHandle && formSchema[storeHandle] !== undefined) {
      formSchema[storeHandle] = yupString().when(countryHandle, {
        is: val => !!val,
        then: s => s.required(),
        otherwise: s => s.notRequired()
      })
    }
  }

  const formStatus = ref(0)

  const {
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values: formValues
  } = useForm({ validationSchema: object(formSchema) })

  if (formSchema && Object.keys(formSchema)?.includes('language')) {
    setFieldValue('language', locale.value)
  }

  if (exists(props.params)) {
    props.params.forEach(p => {
      setFieldValue(p.key, p.value)
    })
  }

  // Conditionally init store selection only for forms that contain the relevant fields
  let storeSelection = null
  if (hasStoreLocator) {
    storeSelection = await useStoreSelection({
      italyId: ITALY_ENTRY_ID,
      excludeItaly: props.storeLocatorExcludeItaly
    })
  }

  // Cascading watchers: country → fetch stores/provinces, province → fetch stores
  if (hasStoreLocator && storeSelection && countryHandle) {
    watch(() => formValues[countryHandle], async (newVal, oldVal) => {
      if (newVal === oldVal) return
      await storeSelection.handleCountryChange(newVal)
      if (provinceHandle) setFieldValue(provinceHandle, '')
      if (storeHandle) setFieldValue(storeHandle, '')
    })

    if (provinceHandle) {
      watch(() => formValues[provinceHandle], async (newVal, oldVal) => {
        if (newVal === oldVal) return
        await storeSelection.handleProvinceChange(newVal)
        if (storeHandle) setFieldValue(storeHandle, '')
      })
    }

    // Pre-select Italy when the current locale is Italian
    if (locale.value === 'it') {
      await nextTick()
      setFieldValue(countryHandle, ITALY_ENTRY_ID)
    }
  }

  /**
   * Resolves the options array for a given field.
   *
   * Priority:
   *  1. Store-locator overrides (country / province / store) — driven by useStoreSelection.
   *     For the store field, value is the human-readable "Name, Address" string rather than
   *     the entry ID, because Formie saves it as plain text (Field_SingleLineText).
   *  2. Field_Entries static entries returned by the Formie GQL query.
   *  3. Field_Dropdown options from Formie, with i18n label overrides where available.
   */
  const getFieldOptions = field => {
    // Priority 0: external overrides from parent component
    if (props.fieldOptionsOverrides[field.name]?.length) {
      return props.fieldOptionsOverrides[field.name]
    }
    if (storeSelection) {
      if (STORE_LOCATOR_COUNTRY_HANDLES.includes(field.name)) return storeSelection.countryOptions.value
      if (STORE_LOCATOR_PROVINCE_HANDLES.includes(field.name)) return storeSelection.provinceOptions.value
      if (STORE_LOCATOR_STORE_HANDLES.includes(field.name)) {
        // Build options from the raw store objects so we can compose a richer submission value.
        // storeOptions (from useStoreSelection) uses s.id as value and is intentionally left
        // unchanged for LandingForm.vue which needs the ID for post-selection store lookup.
        const notInList = { value: 'not-in-list', label: t('forms.labels.storeNotInList') }
        const options = storeSelection.stores.value?.map(s => {
          const displayTitle = getStoreSearchDisplayTitle(s)
          const parts = [ displayTitle, s.storeAddress ].filter(Boolean)
          return {
            value: parts.join(', '),
            label: displayTitle
          }
        }).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })) ?? []
        return [ notInList, ...options ]
      }
    }
    if (field.entries?.length) {
      return field.entries.map(e => ({ value: String(e.id), label: e.title }))
    }
    if (exists(field.options)) {
      return field.options.map(o => ({
        ...o,
        label: te(`forms.labels.${o.value}`) ? t(`forms.labels.${o.value}`) : o.label
      }))
    }
    return undefined
  }

  /**
   * Etichetta di un campo.
   *
   * Di norma viene da `forms.labels.<handle>`, ma alcuni form condividono lo stesso handle
   * pur dovendo mostrare testi diversi: e' il caso di `processingPersonalData`, usato da
   * store locator, promo e landing, dove le pagine rivenditore e landing devono rimandare
   * all'informativa dedicata "Fissa un appuntamento". Si cerca quindi prima una chiave
   * specifica del form (`forms.labels.<formHandle>.<handle>`) e, se non esiste, si ricade
   * su quella condivisa: gli altri form restano invariati.
   */
  const fieldLabel = field => {
    const perForm = `forms.labels.${props.handle}.${field.name}`
    return te(perForm) ? t(perForm) : (t(`forms.labels.${field.name}`) || field.label)
  }

  /**
   * Controls runtime visibility of province and store fields.
   * These are hidden (v-if) instead of display:none so vee-validate unmounts them
   * and stops tracking their values — preventing ghost validation errors.
   */
  const isFieldVisible = field => {
    if (!storeSelection) return true
    if (STORE_LOCATOR_PROVINCE_HANDLES.includes(field.name)) return storeSelection.showProvinceSelect.value
    if (STORE_LOCATOR_STORE_HANDLES.includes(field.name)) return storeSelection.showStoreSelect.value
    return true
  }

  /**
   * Returns the Vue component to use for a field.
   *
   * Province and store fields are declared as Field_SingleLineText in Formie to prevent
   * Formie from validating submitted values against an empty dropdown options list.
   * They must still render as selects on the frontend because their options are injected
   * dynamically by useStoreSelection.
   */
  const getFieldComponent = field => {
    if (storeSelection && (
      STORE_LOCATOR_PROVINCE_HANDLES.includes(field.name)
      || STORE_LOCATOR_STORE_HANDLES.includes(field.name)
    )) return 'InputSelectNative'
    return componentMatcher(field.typeName)
  }

  watch(() => props.params, async nval => {
    await nextTick()
    if (nval) {
      setTimeout(() => {
        nval.forEach(p => {
          setFieldValue(p.key, p.value)
        })
      }, 100)
    }
  }, { immediate: true })

  watch(isSubmitting, nval => {
    emit('submitting', nval)
  })

  const handleSubmission = handleSubmit(async (values, actions) => {
    try {
      let recaptchaToken
      if (recaptchaSettings.value) {
        const { token } = await executeRecaptcha(data.value.submissionMutationName, recaptchaSettings.value?.siteKey)
        recaptchaToken = token
      }

      if (!formSchema) {
        formSchema = data.value ? createFormSchema(data.value.rows) : null
      }

      const query = createFormQuery(formSchema, data.value.submissionMutationName, !!recaptchaSettings.value, fieldTypeNameMap, timeOnlyFields)

      /**
       * Formie expects Field_Entries values as [Int] (array of entry IDs).
       * vee-validate stores the selected value as a string, so we parse and wrap it here.
       *
       * The 'italy' fallback handles stale cached state from before the dealerCountry field
       * was migrated from Field_Dropdown (string value 'italy') to Field_Entries (entry ID).
       * Without this, a cached page load could send a raw 'italy' string to a [Int] field
       * and get a GraphQL type error even though the field UI is correct.
       */
      const processedValues = { ...values }

      // Time-only Date fields: prepend a fixed UTC date so the DateTime value
      // isn't shifted by timezone conversion (e.g. "15:00" → "1970-01-01T15:00:00Z")
      timeOnlyFields.forEach(handle => {
        if (processedValues[handle]) {
          processedValues[handle] = `1970-01-01T${processedValues[handle]}:00Z`
        }
      })

      Object.keys(fieldTypeNameMap).forEach(handle => {
        if (fieldTypeNameMap[handle] === 'Field_Entries' && processedValues[handle] !== undefined) {
          let rawValue = processedValues[handle]
          if (rawValue === 'italy') rawValue = ITALY_ENTRY_ID
          const id = parseInt(rawValue)
          processedValues[handle] = !isNaN(id) ? [ id ] : []
        }
      })

      try {
        await $graphql.default.request(query, {
          ...processedValues,
          ...(recaptchaSettings.value
            ? {
              recaptchaCaptcha: {
                name: 'g-recaptcha-response',
                value: recaptchaToken
              }
            }
            : {})
        })

        formStatus.value = 1

        if (!props.keepSuccessMessage) {
          setTimeout(() => {
            actions.resetForm()
            formStatus.value = 0
            emit('submitted', values)
          }, 5000)
        } else {
          emit('submitted', values)
        }

        // gtm.trackEvent({
        //   event: 'form_submit',
        //   category: 'form',
        //   action: 'submit',
        //   label: `Submit form ${props.handle}`,
        //   ...(values.merchCategory ? { merchCategory: values.merchCategory } : {}),
        //   ...(values.country ? { country: values.country } : {}),
        //   noninteraction: false
        // })
      } catch (err) {
        console.error('[form] ERROR', err)
        formStatus.value = -1
      }
    } catch (error) {
      console.error(error)
    }
  })

  await nextTick()
  emit('load')
</script>

  <style lang="scss" scoped>
    .form-loader {
      @include blocks-y-spacing;

      z-index: 1;

      &.-wrapped {
        @include container;

        @include mq(medium) {
          @include wrapper;
        }
      }

      &__text {
        @include container;

        grid-column: span 12;
        margin-bottom: 40px;
        text-align: center;

        @include mq(desktop) {
          margin-bottom: 70px;
        }
      }

      &.-align-right {
        text-align: right;
      }

      &__title {
        @include title-m;

        grid-column: 1 / span 12;
        margin-bottom: 20px;

        @include mq(desktop) {
          grid-column: 3 / span 8;
        }

        &.-big {
          @include title-l;
        }
      }

      &__abstract {
        grid-column: 1 / span 12;

        @include mq(desktop) {
          grid-column: 3 / span 8;
        }
      }

      &__body {
        grid-column: span 12;
        display: flex;
        flex-direction: column;

        @include mq(desktop) {
          grid-column: 2 / span 10;
          gap: 0;
        }
      }

      &__row {
        @include container;

        padding-bottom: 10px;

        &.-submit {
          padding-top: rem(20px);
        }
      }

      &__field {
        margin-bottom: 20px;
        grid-column: 1 / span 12;

        @include mq(desktop) {
          margin-bottom: 10px;
        }

        &.-hidden {
          display: none;
        }

        &.-col-6 {
          @include mq(desktop) {
            grid-column: span 6;
          }
        }

        &.-col-4 {
          @include mq(desktop) {
            grid-column: span 4;
          }
        }

        &.-col-12 {
          @include mq(desktop) {
            grid-column: span 12;
          }
        }

        &.-col-3 {
          grid-column: span 6;

          @include mq(desktop) {
            grid-column: span 3;
          }
        }

        &.-align-left {
          text-align: left;
        }

        &.-align-center {
          text-align: center;
        }

        &.-align-right {
          text-align: right;
        }
      }

      &__error {
        color: var(--color-red);

        >strong {
          display: block;
          margin-bottom: 4px;
        }
      }

      &__submitted {
        grid-column: span 12;
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;
        margin-bottom: 60px;

        @include mq(desktop) {
          grid-column: 3 / span 8;
        }

        >h4 {
          font-size: rem(42px);
          line-height: normal;
          margin-bottom: 20px;
        }

        >div {
          font-size: rem(32px);
          line-height: normal;
        }
      }

      .cta {
        margin: 0 auto;
        display: flex;
      }
    }
  </style>
